import {getOwnAppVersion} from 'lib/appVersion';
import {get as getCookie} from 'lib/cookies';
import logger from 'lib/logger';
import {session} from 'lib/StorageUtil';

interface HistoryLocationLike {
  pathname: string;
}

export interface HistoryLike {
  location: HistoryLocationLike;
  subscribe: (cb: (args: {location: HistoryLocationLike}) => void) => () => void;
}

interface ReloadAttempt {
  fromVersion: string;
  toVersion: string;
  at: number;
  count: number;
}

const APP_VERSION_ENDPOINT = '/app-version';
const REVIEW_PIN_COOKIE = 'albert_version';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const MIN_CHECK_GAP_MS = 60 * 1000;
const MAX_CONSECUTIVE_INVALID_RESPONSES = 3;
const RELOAD_RETRY_COOLDOWN_MS = 10 * 60 * 1000;
const MAX_RELOAD_ATTEMPTS = 2;
const RELOAD_ATTEMPT_STORAGE_KEY = 'staleAppReload:lastAttempt';
const STORAGE_PROBE_KEY = 'staleAppReload:probe';
const FETCH_TIMEOUT_MS = 10 * 1000;
const VERSION_PATTERN = /^[\w.-]{1,64}$/;

let holdCount = 0;

export function holdStaleAppReload(): () => void {
  holdCount += 1;
  let released = false;
  return () => {
    if (!released) {
      released = true;
      holdCount -= 1;
    }
  };
}

function hasDurableAttemptStorage(): boolean {
  session.set(STORAGE_PROBE_KEY, true);
  const roundTripped = session.get(STORAGE_PROBE_KEY) === true;
  session.remove(STORAGE_PROBE_KEY);
  return roundTripped;
}

async function fetchServedVersion(): Promise<string | null> {
  const response = await fetch(APP_VERSION_ENDPOINT, {
    cache: 'no-store',
    signal: AbortSignal.timeout(FETCH_TIMEOUT_MS)
  });
  if (!response.ok) {
    if (response.status === 408 || response.status === 429 || response.status >= 500) {
      throw new Error(`app-version responded ${response.status}`);
    }
    return null;
  }
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/plain')) {
    return null;
  }
  const body = (await response.text()).trim();
  return VERSION_PATTERN.test(body) ? body : null;
}

interface StaleAppReloadOptions {
  reloadPage?: () => void;
}

export function initStaleAppReload(
  history: HistoryLike,
  {reloadPage = () => window.location.reload()}: StaleAppReloadOptions = {}
): () => void {
  const ownVersion = getOwnAppVersion();
  if (!ownVersion || getCookie(REVIEW_PIN_COOKIE) || !hasDurableAttemptStorage()) {
    return () => {};
  }

  let staleVersion: string | null = null;
  let lastCheckAt = Date.now();
  let checkInFlight = false;
  let consecutiveInvalidResponses = 0;
  let lastPathname = history.location.pathname;

  const approveReloadAttempt = (fromVersion: string, toVersion: string): boolean => {
    const previous = session.get(RELOAD_ATTEMPT_STORAGE_KEY) as ReloadAttempt | null | undefined;
    const samePair =
      previous != null && previous.fromVersion === fromVersion && previous.toVersion === toVersion;
    if (samePair) {
      if (previous.count >= MAX_RELOAD_ATTEMPTS) {
        return false;
      }
      if (Date.now() - previous.at < RELOAD_RETRY_COOLDOWN_MS) {
        return false;
      }
    }
    session.set(RELOAD_ATTEMPT_STORAGE_KEY, {
      fromVersion,
      toVersion,
      at: Date.now(),
      count: samePair ? previous.count + 1 : 1
    } satisfies ReloadAttempt);
    return true;
  };

  const intervalId = setInterval(check, POLL_INTERVAL_MS);

  const stopPolling = () => {
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };

  async function check(): Promise<void> {
    if (checkInFlight || staleVersion !== null) {
      return;
    }
    checkInFlight = true;
    lastCheckAt = Date.now();
    let servedVersion: string | null;
    try {
      servedVersion = await fetchServedVersion();
    } catch {
      return;
    } finally {
      checkInFlight = false;
    }

    if (servedVersion === null) {
      consecutiveInvalidResponses += 1;
      if (consecutiveInvalidResponses >= MAX_CONSECUTIVE_INVALID_RESPONSES) {
        stopPolling();
        logger.warn(
          'staleAppReload: /app-version repeatedly answered without a valid version; disabling for this page load.'
        );
      }
      return;
    }

    consecutiveInvalidResponses = 0;
    if (servedVersion !== ownVersion) {
      staleVersion = servedVersion;
      stopPolling();
    }
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && Date.now() - lastCheckAt >= MIN_CHECK_GAP_MS) {
      check();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  const unsubscribe = history.subscribe(({location}) => {
    const pathnameChanged = location.pathname !== lastPathname;
    lastPathname = location.pathname;
    if (!pathnameChanged || staleVersion === null || holdCount > 0) {
      return;
    }
    if (getCookie(REVIEW_PIN_COOKIE)) {
      return;
    }
    if (!approveReloadAttempt(ownVersion, staleVersion)) {
      return;
    }
    reloadPage();
  });

  return () => {
    stopPolling();
    unsubscribe();
  };
}
